import IconExchange from "@/components/svg/IconExchange";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { DragHandleDots1Icon } from "@radix-ui/react-icons";
import IconDrag from "@/components/svg/IconDrag";
import { createPortal } from "react-dom";
import Loading from "@/shared/common/Loading";
import { toast } from "sonner";

const DragPortal = ({ children }) => {
     return createPortal(children, document.body);
};

const FieldsDnd = (props) => {
     const { viewFields, setActiveFields, activeFields, setSearchValue, searchValue, isLoading, isLoadingFields, viewId } = props;
     const [isOpen, setIsOpen] = useState({
          defaultFields: false,
          customFields: false,
          toleranceFields: false,
     });

     const handleAddAll = (group) => {
          if (!viewId) {
               toast.error('Please select a view first');
               return;
          }
          if (group?.fields && group.fields.length > 0) {
               const fieldsWithSource = group.fields.map(field => ({
                    ...field,
                    _sourceGroup: group.group
               }));

               const newActiveFields = [...activeFields, ...fieldsWithSource];
               setActiveFields(newActiveFields);

               // Remove all fields from this group in viewFields
               if (props.setViewFields) {
                    const newViewFields = [...viewFields];
                    const groupIndex = newViewFields.findIndex(g => g.group === group.group);
                    if (groupIndex !== -1) {
                         newViewFields[groupIndex].fields = [];
                         props.setViewFields(newViewFields);
                    }
               }
          }
     };

     const handleDragEnd = (result) => {
          if (!viewId) { toast.error('Please select a view first'); return };
          const { source, destination } = result;
          if (!destination) return;

          // If dragging from viewFields to activeFields
          if (source.droppableId === 'viewFields' && destination.droppableId === 'activeFields') {
               // Find the group and field being dragged
               // We need to calculate the actual group and field index from the flat index
               let groupIndex = 0;
               let fieldIndex = 0;
               let currentIndex = 0;

               // Find which group and field the source index corresponds to
               for (let i = 0; i < viewFields.length; i++) {
                    if (isOpen[viewFields[i].group]) {
                         const groupFields = viewFields[i].fields || [];
                         if (currentIndex + groupFields.length > source.index) {
                              groupIndex = i;
                              fieldIndex = source.index - currentIndex;
                              break;
                         }
                         currentIndex += groupFields.length;
                    }
               }

               // Get the field from viewFields
               const draggedField = viewFields[groupIndex]?.fields[fieldIndex];

               if (draggedField) {
                    // Add to activeFields with source group information
                    const fieldWithSource = {
                         ...draggedField,
                         _sourceGroup: viewFields[groupIndex]?.group // Track which group this field came from
                    };
                    const newActiveFields = [...activeFields, fieldWithSource];
                    setActiveFields(newActiveFields);

                    // Remove from viewFields
                    if (props.setViewFields) {
                         const newViewFields = [...viewFields];
                         newViewFields[groupIndex].fields.splice(fieldIndex, 1);
                         props.setViewFields(newViewFields);
                    }
               }
          }
          // If dragging from activeFields back to viewFields
          else if (source.droppableId === 'activeFields' && destination.droppableId === 'viewFields') {
               // Get the field being dragged back
               const draggedField = activeFields[source.index];

               if (draggedField) {
                    // Remove from activeFields
                    const newActiveFields = [...activeFields];
                    newActiveFields.splice(source.index, 1);
                    setActiveFields(newActiveFields);

                    // Add back to viewFields (find the appropriate group)
                    if (props.setViewFields) {
                         const newViewFields = [...viewFields];

                         // Use the tracked source group to place the field back in the correct group
                         let targetGroupIndex = 0; // Default to first group

                         if (draggedField._sourceGroup) {
                              // Find the group this field originally came from
                              const sourceGroupIndex = newViewFields.findIndex(group => group.group === draggedField._sourceGroup);
                              if (sourceGroupIndex !== -1) {
                                   targetGroupIndex = sourceGroupIndex;
                              }
                         } else {
                              // Fallback logic using fieldType if source group is not tracked
                              if (draggedField.fieldType === 'toleranceFields') {
                                   const toleranceGroupIndex = newViewFields.findIndex(group => group.group === 'toleranceFields');
                                   if (toleranceGroupIndex !== -1) {
                                        targetGroupIndex = toleranceGroupIndex;
                                   }
                              } else if (draggedField.fieldType === 'customFields') {
                                   const customGroupIndex = newViewFields.findIndex(group => group.group === 'customFields');
                                   if (customGroupIndex !== -1) {
                                        targetGroupIndex = customGroupIndex;
                                   }
                              } else if (draggedField.fieldType === 'defaultFields') {
                                   const defaultGroupIndex = newViewFields.findIndex(group => group.group === 'defaultFields');
                                   if (defaultGroupIndex !== -1) {
                                        targetGroupIndex = defaultGroupIndex;
                                   }
                              }
                         }

                         // Remove the source group tracking before adding back
                         const { _sourceGroup, ...fieldWithoutSource } = draggedField;

                         // Add the field back to the appropriate group
                         newViewFields[targetGroupIndex].fields.push(fieldWithoutSource);
                         props.setViewFields(newViewFields);
                    }
               }
          }
          // If dragging within activeFields (reordering)
          else if (source.droppableId === 'activeFields' && destination.droppableId === 'activeFields') {
               const sourceIndex = source.index;
               const destinationIndex = destination.index;
               const newActiveFields = [...activeFields];
               const [removed] = newActiveFields.splice(sourceIndex, 1);
               newActiveFields.splice(destinationIndex, 0, removed);
               setActiveFields(newActiveFields);
          }
     }
     return (
          <DragDropContext onDragEnd={handleDragEnd}>
               <div className="border border-borderV1 h-[335px] w-[calc(47.5%-6px)]">
                    <div className="py-1.5 h-[30px] flex items-center justify-center border-b border-borderV1 px-3">
                         <p className="text-center text-xs text-textV1">Fields</p>
                    </div>
                    <div className="border-b border-borderV1 flex items-center justify-center  px-3 h-[30px]">
                         <div className="relative w-full">
                              <span className="absolute top-1/2 start-0 -translate-y-1/2">
                                   <Search className="w-4 h-4 text-textV1" />
                              </span>
                              <Input
                                   className="px-3 !ps-6 border-none h-full py-0 text-xs"
                                   placeholder="Search Fields"
                                   type="text"
                                   value={searchValue}
                                   onChange={(e) => setSearchValue(e.target.value)}
                              />
                         </div>
                    </div>
                    <div className="h-[calc(100%-60px)] bg-backgroundV1">
                         {isLoading ? (
                              <div className="h-full flex items-center justify-center">
                                   <Loading height={20} width={20} />
                              </div>
                         ) : (
                              <Droppable
                                   droppableId="viewFields"
                              >
                                   {(provided) => (
                                        <ul
                                             className="h-full overflow-y-auto p-1 overflow-x-hidden scrollbar-width-thin"
                                             ref={provided.innerRef}
                                             {...provided.droppableProps}
                                        >
                                             {(() => {
                                                  let flatIndex = 0;
                                                  return viewFields && viewFields.map((group, groupIndex) => (
                                                       <li key={`${group?.group}-${groupIndex}`} className="mb-0.5">
                                                            <div className="flex items-center justify-between bg-white border border-gray-200 rounded-[4px] px-3 py-1 cursor-pointer" onClick={() => setIsOpen({ ...isOpen, [group?.group]: !isOpen[group?.group] })}>
                                                                 <span className="text-[12px] font-medium text-gray-700">
                                                                      {group?.group === 'defaultFields' ? 'General' :
                                                                           group?.group === 'customFields' ? 'Byod Fields' :
                                                                                group?.group === 'toleranceFields' ? 'Tolerance Fields' : group?.group} - {group?.fields?.length || 0}
                                                                 </span>
                                                                 <Button
                                                                      variant=""
                                                                      size="sm"
                                                                      className="px-2 py-1 rounded-[4px]"
                                                                      onClick={(e) => { e.stopPropagation(); handleAddAll(group) }}
                                                                      disabled={group?.fields?.length === 0}
                                                                 >
                                                                      Add All
                                                                 </Button>
                                                            </div>
                                                            {isOpen[group?.group] && <div className="bg-white rounded-[4px]">
                                                                 {group?.fields && group?.fields?.map((field) => {
                                                                      const currentFlatIndex = flatIndex++;
                                                                      return (
                                                                           <Draggable
                                                                                key={field?._id}
                                                                                draggableId={field?._id}
                                                                                index={currentFlatIndex}
                                                                           >
                                                                                {(provided, snapshot) => {
                                                                                     const child = (
                                                                                          <li
                                                                                               ref={provided.innerRef}
                                                                                               {...provided.draggableProps}
                                                                                               {...provided.dragHandleProps}
                                                                                               className={`flex items-center gap-2 px-3 py-2 hover:bg-gray-100 cursor-move border-b border-gray-200 last:border-b-0 ${snapshot?.isDragging && "bg-white"}`}
                                                                                               style={{
                                                                                                    ...provided.draggableProps.style,
                                                                                               }}
                                                                                          >
                                                                                               <IconDrag className="w-3 h-3 text-gray-400" />
                                                                                               <span className="text-[11px] text-gray-700">
                                                                                                    {`${field?.toleranceParameter ? field?.name + ' - ' + field?.field : field?.name}`}
                                                                                               </span>
                                                                                          </li>
                                                                                     );

                                                                                     return snapshot?.isDragging ? <DragPortal>{child}</DragPortal> : child;
                                                                                }}
                                                                           </Draggable>
                                                                      );
                                                                 })}
                                                            </div>}
                                                       </li>
                                                  ));
                                             })()}
                                             {provided?.placeholder}
                                        </ul>
                                   )}
                              </Droppable>
                         )}
                    </div>

               </div>
               <span className="flex items-center justify-center w-[calc(5%-6px)]">
                    <IconExchange className="w-6 h-6 text-textV2" />
               </span>
               <div className="h-[335px] border border-borderV1 w-[calc(47.5%-6px)]">
                    <div className="py-1.5 border-b h-[30px] flex items-center justify-center px-3 border-borderV1">
                         <p className="text-center text-xs text-textV1">View Fields</p>
                    </div>
                    <div className="h-[calc(100%-30px)] overflow-hidden bg-backgroundV1">
                         {isLoadingFields ? (
                              <div className="h-full flex items-center justify-center">
                                   <Loading height={20} width={20} />
                              </div>
                         ) : (
                              <Droppable
                                   droppableId="activeFields"
                              >
                                   {(provided) => (
                                        <ul
                                             className="h-full overflow-y-auto p-1 overflow-x-hidden scrollbar-width-thin"
                                             ref={provided.innerRef}
                                             {...provided.droppableProps}
                                        >
                                             {activeFields?.length > 0 ? activeFields?.map((field, fieldIndex) => (
                                                  <Draggable
                                                       key={field?._id}
                                                       draggableId={field?._id}
                                                       index={fieldIndex}
                                                  >
                                                       {(provided, snapshot) => {
                                                            const child = (
                                                                 <li
                                                                      ref={provided.innerRef}
                                                                      {...provided.draggableProps}
                                                                      {...provided.dragHandleProps}
                                                                      className={`flex items-center gap-2 px-3 py-2 hover:bg-gray-100 cursor-move bg-white border border-gray-200 rounded-[4px] mb-1 ${snapshot?.isDragging && "bg-white"}`}
                                                                      style={{
                                                                           ...provided.draggableProps.style,
                                                                      }}
                                                                 >
                                                                      <IconDrag className="w-3 h-3 text-gray-400" />
                                                                      <span className="text-[11px] text-gray-700">
                                                                           {`${field?.toleranceParameter ? field?.name + ' - ' + field?.field : field?.name}`}
                                                                      </span>
                                                                 </li>
                                                            )
                                                            return snapshot?.isDragging ? <DragPortal>{child}</DragPortal> : child
                                                       }}
                                                  </Draggable>
                                             )) : <div className="flex items-center justify-center h-full">
                                                  <p className="text-gray-500 text-xs">No fields found!</p>
                                             </div>}
                                             {provided?.placeholder}
                                        </ul>
                                   )}
                              </Droppable>
                         )}
                    </div>
               </div>
          </DragDropContext>
     )
}
export default FieldsDnd;

